// Settings (the gear button, or O): sound, interface, chat and touch controls. Saved on this device.
import type { Channel } from '../../../shared/chat.js';

export type NameMode = 'all' | 'crowd' | 'party' | 'off';
export type Theme = 'cozy' | 'night' | 'contrast';
export type OthersMode = 'all' | 'near30' | 'friends';
export interface Settings {
  master: number; music: number; sfx: number; ui: number; others: boolean; musicOn: boolean;
  uiScale: number; theme: Theme; names: NameMode; bubbles: boolean; stats: boolean; crowd: OthersMode; quiet: boolean;
  padSize: number; leftHanded: boolean;
  chatSize: number; chatLines: number; chatBg: number; chatHide: Channel[];
  /** Solo battles hold still while your bar is full. */
  waitMode: boolean;
  /** Rain, cloud shadows, lightning and fog drawn over the world (off: just the light changes). */
  weatherFx: boolean;
  /** The camera's zoom (pinch, or ctrl + scroll): 1 = normal. */
  zoom: number;
  /** The ferry and the airship drawn going about their routes (world map, minimap, overworld). */
  vessels: boolean;
  /** Party settings each person keeps (the party sees them): take a Raise without being asked; a potion below this much health (0 = off). */
  autoRaise: boolean; autoPotion: 0 | 25 | 40 | 50;
  /** Coloured rings on the ground under party members, by role: in battle only, always, or off. */
  roleRings: 'battle' | 'always' | 'off';
  /** What Tab picks first: the nearest, the weakest, or the party leader's marked target. */
  tabOrder: 'near' | 'low' | 'mark';
}
const KEY = 'tt.settings.v1';
const coarse = matchMedia('(pointer:coarse)').matches;
export const DEFAULTS: Settings = {
  master: 0.8, music: 0.45, sfx: 0.8, ui: 0.6, others: true, musicOn: true,
  uiScale: 1, theme: 'cozy', names: 'crowd', bubbles: true, stats: false, crowd: 'all', quiet: false,
  padSize: 1, leftHanded: false,
  chatSize: coarse ? 0.9 : 1, chatLines: coarse ? 3 : 9, chatBg: 0.35, chatHide: [], waitMode: false, weatherFx: true,
  zoom: 1, vessels: true,
  autoRaise: false, autoPotion: 0, roleRings: 'battle', tabOrder: 'near',
};

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; }

export class SettingsUI {
  s: Settings;
  onChange: (s: Settings) => void = () => {};
  /** "Move windows": the layout editor. */
  onLayout: () => void = () => {};
  /** Extra tabs added by other parts of the UI (the Mods tab). */
  tabs: { id: string; label: string; render: (box: HTMLElement) => void }[] = [];
  private win = $('settings'); private tab = 'sound';

  constructor() {
    let saved: Partial<Settings> = {};
    try { saved = JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { /* private mode or bad data */ }
    this.s = { ...DEFAULTS, ...saved };
    $('settingsBtn').addEventListener('click', () => this.toggle());
    $('settingsClose').addEventListener('click', () => this.toggle(false));
    addEventListener('keydown', e => {
      if (/INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName || '')) return;
      if (e.code === 'KeyO' && !e.repeat) { e.preventDefault(); this.toggle(); }
      if (e.code === 'Escape' && this.open) this.toggle(false);
    });
  }

  get open(): boolean { return !this.win.hidden; }
  toggle(force?: boolean): void { this.win.hidden = !(force ?? this.win.hidden); if (this.open) this.render(); }

  set<K extends keyof Settings>(k: K, v: Settings[K]): void {
    this.s = { ...this.s, [k]: v };
    try { localStorage.setItem(KEY, JSON.stringify(this.s)); } catch { /* not saved, still applied */ }
    this.onChange(this.s);
  }

  private render(): void {
    const tabs = [{ id: 'sound', label: 'Sound' }, { id: 'interface', label: 'Interface' }, { id: 'battle', label: 'Battle & party' }, { id: 'chat', label: 'Chat' }, ...this.tabs];
    const bar = $('settingsTabs'); bar.replaceChildren();
    for (const t of tabs) {
      const b = el('button', '', t.label); b.type = 'button'; b.setAttribute('role', 'tab'); b.setAttribute('aria-selected', String(t.id === this.tab));
      b.addEventListener('click', () => { this.tab = t.id; this.render(); }); bar.append(b);
    }
    const box = $('settingsBody'); box.replaceChildren();
    const extra = this.tabs.find(t => t.id === this.tab);
    if (extra) return extra.render(box);
    const s = this.s;
    if (this.tab === 'sound') {
      box.append(
        this.slider('Master volume', 'master', 0, 1, 0.05, v => Math.round(v * 100) + '%'),
        this.check('Music', 'musicOn'),
        this.slider('Music volume', 'music', 0, 1, 0.05, v => Math.round(v * 100) + '%'),
        this.slider('Effects', 'sfx', 0, 1, 0.05, v => Math.round(v * 100) + '%'),
        this.slider('Menus and typing', 'ui', 0, 1, 0.05, v => Math.round(v * 100) + '%'),
        this.check("Other players' sounds (dashes, attacks)", 'others'),
        el('p', 'set-note', 'Sound pauses while the game is in the background.'));
    } else if (this.tab === 'interface') {
      box.append(
        this.slider('Interface size', 'uiScale', 0.8, 1.4, 0.05, v => Math.round(v * 100) + '%'),
        this.choice('Theme', 'theme', [['cozy', 'Cozy'], ['night', 'Night'], ['contrast', 'High contrast']]),
        el('h3', 'set-h', 'Crowds'),
        this.choice('Show other players', 'crowd', [['all', 'Everyone'], ['near30', 'Nearest 30 (others faded)'], ['friends', 'Friends + party (others faded)']]),
        this.choice('Name tags', 'names', [['all', 'Everyone'], ['crowd', 'Nearest + party'], ['party', 'Party only'], ['off', 'Off']]),
        this.check('Prefer quieter layers (when you enter a zone)', 'quiet'),
        this.check('Speech bubbles', 'bubbles'),
        this.check('Weather effects (rain, cloud shadows, lightning, fog)', 'weatherFx'),
        this.check('Show ships and airships on the maps', 'vessels'),
        this.slider('Camera zoom (pinch with two fingers; double-tap with two to reset)', 'zoom', 0.7, 1.8, 0.05, v => Math.round(v * 100) + '%'),
        this.check('Network stats (F3)', 'stats'),
        el('h3', 'set-h', 'Touch controls'),
        this.slider('Button size', 'padSize', 0.8, 1.3, 0.05, v => Math.round(v * 100) + '%'),
        this.check('Left-handed (buttons on the left)', 'leftHanded'),
        el('h3', 'set-h', 'Layout'),
        this.button('Move windows…', 'Drag the chat, minimap, party list and quest box where you like.', () => { this.onLayout(); }),
        this.reset());
    } else if (this.tab === 'battle') {
      box.append(
        el('h3', 'set-h', 'Party settings'),
        el('p', 'set-note', 'Your party sees these next to your name in the party list, so you can talk them over.'),
        this.check('Accept a Raise without being asked', 'autoRaise'),
        this.choice('Drink a potion by myself below', 'autoPotion', [['0', 'Off'], ['25', '25% health'], ['40', '40% health'], ['50', '50% health']]),
        el('p', 'set-note', 'Your battle formation is picked by the party leader in the party window (P).'),
        el('h3', 'set-h', 'Targeting'),
        this.choice('Tab picks first', 'tabOrder', [['near', 'The nearest'], ['low', 'The weakest (lowest health)'], ['mark', "The leader's marked target"]]),
        el('p', 'set-note', 'Tab (or R1) cycles targets; Shift+Tab goes back. A party leader marks a target with T in battle, and everyone set to "marked target" jumps to it.'),
        el('h3', 'set-h', 'Role rings'),
        this.choice('Coloured rings under your party', 'roleRings', [['battle', 'In battle'], ['always', 'Always'], ['off', 'Off']]),
        el('p', 'set-note', 'Blue for protectors, green for healers, red for damage. Adventurers have none.'));
    } else if (this.tab === 'chat') {
      box.append(
        this.slider('Text size', 'chatSize', 0.75, 1.4, 0.05, v => Math.round(v * 100) + '%'),
        this.slider('Lines shown', 'chatLines', 3, 16, 1, v => String(v)),
        this.slider('Background', 'chatBg', 0, 0.8, 0.05, v => v ? Math.round(v * 100) + '%' : 'None'));
      const h = el('fieldset', 'set-chans'); h.append(el('legend', '', 'Show these channels'));
      for (const [ch, label] of [['say', 'Say'], ['zone', 'Zone'], ['faction', 'Faction'], ['party', 'Party'], ['alliance', 'Alliance'], ['whisper', 'Whispers']] as [Channel, string][]) {
        const l = el('label', 'set-check'), i = el('input'); i.type = 'checkbox'; i.checked = !s.chatHide.includes(ch);
        i.addEventListener('change', () => this.set('chatHide', i.checked ? this.s.chatHide.filter(c => c !== ch) : [...this.s.chatHide, ch]));
        l.append(i, document.createTextNode(label)); h.append(l);
      }
      box.append(h);
    }
  }

  private button(label: string, note: string, fn: () => void): HTMLElement {
    const w = el('div', 'set-row'), b = el('button', 'btn', label); b.type = 'button'; b.style.width = 'auto'; b.addEventListener('click', fn);
    w.append(b, el('p', 'set-note', note)); return w;
  }
  private slider(label: string, k: keyof Settings, min: number, max: number, step: number, fmt: (v: number) => string): HTMLElement {
    const l = el('label', 'set-slider'), out = el('b', '', fmt(this.s[k] as number)), i = el('input');
    i.type = 'range'; i.min = String(min); i.max = String(max); i.step = String(step); i.value = String(this.s[k]);
    i.addEventListener('input', () => { const v = Number(i.value); out.textContent = fmt(v); this.set(k, v as never); });
    l.append(el('span', '', label), out, i); return l;
  }
  private check(label: string, k: keyof Settings): HTMLElement {
    const l = el('label', 'set-check'), i = el('input'); i.type = 'checkbox'; i.checked = !!this.s[k];
    i.addEventListener('change', () => this.set(k, i.checked as never));
    l.append(i, document.createTextNode(label)); return l;
  }
  private choice(label: string, k: keyof Settings, opts: [string, string][]): HTMLElement {
    const l = el('label', 'set-choice'), sel = el('select');
    for (const [v, t] of opts) { const o = el('option', '', t); o.value = v; o.selected = String(this.s[k]) === v; sel.append(o); }
    sel.addEventListener('change', () => this.set(k, (typeof this.s[k] === 'number' ? Number(sel.value) : sel.value) as never));
    l.append(el('span', '', label), sel); return l;
  }
  private reset(): HTMLElement {
    const b = el('button', 'btn', 'Reset all settings'); b.type = 'button';
    b.addEventListener('click', () => { this.s = { ...DEFAULTS }; try { localStorage.removeItem(KEY); } catch { /* */ } this.onChange(this.s); this.render(); });
    return b;
  }
}
